import React, { useState, useRef } from 'react';
import ReactCrop, { type Crop, type PixelCrop, centerCrop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import { getCroppedImg } from '../lib/cropUtils';
import { RotateCcw, RotateCw, Check, X, Crop as CropIcon, RefreshCw, Loader2 } from 'lucide-react';
import { cn } from '../lib/utils';
import { MealType } from '../types';

interface MealCropperProps {
  image: string;
  onCropComplete: (croppedImage: string, selectedMealType?: MealType) => void;
  onCancel: () => void;
  selectedMealType?: MealType;
  onSelectMealType?: (mealType: MealType) => void;
  lang?: 'en-US' | 'ua' | string;
}

const MealCropper: React.FC<MealCropperProps> = ({ 
  image, 
  onCropComplete, 
  onCancel,
  selectedMealType = 'lunch',
  onSelectMealType,
  lang = 'en-US'
}) => {
  const [crop, setCrop] = useState<Crop>();
  const [completedCrop, setCompletedCrop] = useState<PixelCrop>();
  const [rotation, setRotation] = useState<number>(0);
  const [isProcessing, setIsProcessing] = useState<boolean>(false);
  const [localMealType, setLocalMealType] = useState<MealType>(selectedMealType);
  const imgRef = useRef<HTMLImageElement>(null);

  const onImageLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
    const { width, height } = e.currentTarget;
    const initialCrop = centerCrop(
      {
        unit: '%',
        width: 90,
        height: 90,
      },
      width,
      height,
    );
    setCrop(initialCrop);
  };

  const handleRotateLeft = () => {
    setRotation((prev) => (prev - 90 + 360) % 360);
  };

  const handleRotateRight = () => {
    setRotation((prev) => (prev + 90) % 360);
  };

  const handleResetCrop = () => {
    setRotation(0);
    if (imgRef.current) {
      const { width, height } = imgRef.current;
      setCrop(centerCrop(
        {
          unit: '%',
          width: 95,
          height: 95,
        },
        width,
        height,
      ));
    }
  };

  const handleSave = async () => {
    if (isProcessing) return;
    setIsProcessing(true);

    try {
      if (onSelectMealType) {
        onSelectMealType(localMealType);
      }
      if (completedCrop && imgRef.current) {
        const imageElement = imgRef.current;
        const scaleX = imageElement.naturalWidth / imageElement.width;
        const scaleY = imageElement.naturalHeight / imageElement.height;

        const pixelCrop = {
          x: Math.round(completedCrop.x * scaleX),
          y: Math.round(completedCrop.y * scaleY),
          width: Math.round(completedCrop.width * scaleX),
          height: Math.round(completedCrop.height * scaleY),
        };

        const croppedImage = await getCroppedImg(image, pixelCrop, rotation);
        onCropComplete(croppedImage, localMealType);
      } else if (rotation !== 0) {
        const rotatedImage = await getCroppedImg(image, undefined, rotation);
        onCropComplete(rotatedImage, localMealType);
      } else {
        onCropComplete(image, localMealType);
      }
    } catch (e) {
      console.error("MealCropper processing error, fallback to source:", e);
      onCropComplete(image, localMealType);
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[70] bg-black/95 backdrop-blur-xl flex flex-col overflow-hidden animate-fadeIn">
      {/* Header Bar */}
      <div className="px-4 py-3 bg-zinc-950/80 border-b border-zinc-800/80 flex items-center justify-between z-10">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-full bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center text-emerald-500">
            <CropIcon className="w-4 h-4" />
          </div>
          <div>
            <span className="text-xs font-black uppercase tracking-wider text-zinc-100">
              {lang === 'ua' ? 'КАДРУВАННЯ ТА ТЕГИ' : 'ADJUST & CROP'}
            </span>
            <p className="text-[10px] text-zinc-400">
              {lang === 'ua' ? 'Розмістіть страву в центрі для 98%+ точності AI' : 'Position the food in the center for 98%+ AI recognition'}
            </p>
          </div>
        </div>

        {/* Rotation & Reset Actions */}
        <div className="flex items-center gap-1.5">
          <button
            type="button"
            onClick={handleRotateLeft}
            className="p-2 rounded-xl bg-zinc-900 border border-zinc-800 text-zinc-300 hover:text-white hover:bg-zinc-800 transition-colors"
            title="Rotate Left 90°"
          >
            <RotateCcw className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={handleRotateRight}
            className="p-2 rounded-xl bg-zinc-900 border border-zinc-800 text-zinc-300 hover:text-white hover:bg-zinc-800 transition-colors"
            title="Rotate Right 90°"
          >
            <RotateCw className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={handleResetCrop}
            className="p-2 rounded-xl bg-zinc-900 border border-zinc-800 text-zinc-300 hover:text-white hover:bg-zinc-800 transition-colors"
            title="Reset Crop"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Main Viewport */}
      <div className="flex-grow relative flex items-center justify-center p-4 overflow-hidden select-none touch-none">
        <div 
          className="relative max-h-[68vh] max-w-full flex items-center justify-center transition-transform duration-200"
          style={{ transform: `rotate(${rotation}deg)` }}
        >
          <ReactCrop
            crop={crop}
            onChange={(_, percentCrop) => setCrop(percentCrop)}
            onComplete={(c) => setCompletedCrop(c)}
            className="max-h-full max-w-full rounded-2xl overflow-hidden shadow-2xl"
          >
            <img
              ref={imgRef}
              src={image}
              alt="Meal to crop"
              decoding="async"
              onLoad={onImageLoad}
              className="max-h-[65vh] w-auto max-w-full object-contain rounded-xl"
            />
          </ReactCrop>
        </div>
      </div>

      {/* Meal Tag Selection Bar */}
      <div className="px-4 py-3 bg-zinc-950 border-t border-zinc-800/80 flex flex-col gap-2 relative z-10">
        <div className="flex items-center justify-between">
          <span className="text-[11px] font-black uppercase tracking-widest text-zinc-400">
            {lang === 'ua' ? 'Тег прийому їжі' : 'Meal Tag'}
          </span>
          <span className="text-[10px] font-extrabold text-emerald-400 uppercase tracking-wider bg-emerald-500/10 px-2 py-0.5 rounded-full border border-emerald-500/20">
            {lang === 'ua'
              ? (localMealType === 'breakfast' ? 'Сніданок' : localMealType === 'lunch' ? 'Обід' : localMealType === 'dinner' ? 'Вечеря' : 'Перекус')
              : localMealType.toUpperCase()}
          </span>
        </div>
        <div className="grid grid-cols-4 gap-2">
          {(['breakfast', 'lunch', 'dinner', 'snack'] as const).map((type) => {
            const labels = {
              breakfast: lang === 'ua' ? 'Сніданок' : 'Breakfast',
              lunch: lang === 'ua' ? 'Обід' : 'Lunch',
              dinner: lang === 'ua' ? 'Вечеря' : 'Dinner',
              snack: lang === 'ua' ? 'Перекус' : 'Snack'
            };
            const icons = {
              breakfast: '🌅',
              lunch: '☀️',
              dinner: '🌙',
              snack: '🍪'
            };
            const isSelected = localMealType === type;
            return (
              <button
                key={type}
                type="button"
                onClick={() => {
                  setLocalMealType(type);
                  if (onSelectMealType) onSelectMealType(type);
                }}
                className={cn(
                  "py-2.5 px-1.5 rounded-xl text-[10px] sm:text-xs font-bold uppercase tracking-wider transition-all flex flex-col sm:flex-row items-center justify-center gap-1 border cursor-pointer",
                  isSelected
                    ? "bg-emerald-500 text-white border-emerald-400 shadow-lg shadow-emerald-500/30 font-black scale-[1.02]"
                    : "bg-zinc-900/90 text-zinc-400 border-zinc-800 hover:border-zinc-700 hover:text-zinc-200"
                )}
              >
                <span className="text-xs">{icons[type]}</span>
                <span className="truncate">{labels[type]}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Footer Controls */}
      <div className="p-4 pb-[max(1rem,env(safe-area-inset-bottom))] flex gap-3 bg-zinc-950/95 border-t border-zinc-800 relative z-10">
        <button
          type="button"
          onClick={onCancel}
          disabled={isProcessing}
          className="flex-1 py-3.5 px-4 bg-zinc-900 hover:bg-zinc-800 active:scale-98 text-zinc-300 hover:text-white rounded-2xl font-bold uppercase tracking-wider text-xs transition-all border border-zinc-800 flex items-center justify-center gap-2 cursor-pointer"
        >
          <X className="w-4 h-4" />
          <span>{lang === 'ua' ? 'Скасувати' : 'Cancel'}</span>
        </button>

        <button
          type="button"
          onClick={handleSave}
          disabled={isProcessing}
          className="flex-[2] py-3.5 px-4 bg-emerald-500 hover:bg-emerald-400 active:scale-98 text-white rounded-2xl font-black uppercase tracking-widest text-xs transition-all shadow-lg shadow-emerald-500/30 flex items-center justify-center gap-2 cursor-pointer"
        >
          {isProcessing ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              <span>{lang === 'ua' ? 'Обробка...' : 'Processing...'}</span>
            </>
          ) : (
            <>
              <Check className="w-4 h-4 stroke-[3]" />
              <span className="whitespace-nowrap">{lang === 'ua' ? 'ПІДТВЕРДИТИ' : 'CONFIRM'}</span>
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default MealCropper;
